import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  throw new Error(
    'Не заданы VITE_SUPABASE_URL и VITE_SUPABASE_ANON_KEY. Скопируйте .env.example в .env.',
  );
}

export const supabase: SupabaseClient = createClient(url, anonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    // Отдельный ключ хранения: сессия персонала не конфликтует между тенантами
    // в одном браузере.
    storageKey: 'shawarma-staff-auth',
  },
  realtime: {
    params: { eventsPerSecond: 5 },
  },
  global: {
    headers: { 'x-application-name': 'shawarma-pwa' },
  },
});

/**
 * Разворачивает ошибку Postgres в сообщение для пользователя.
 *
 * RPC кидают исключения с hint-кодами (`slot_full`, `item_unavailable`).
 * По hint подбирается действие в UI, по message — текст, уже написанный
 * по-русски на стороне БД.
 */
export interface RpcError {
  hint: string | null;
  message: string;
  isNetwork: boolean;
}

export function toRpcError(error: unknown): RpcError {
  if (!error || typeof error !== 'object') {
    return { hint: null, message: 'Неизвестная ошибка', isNetwork: false };
  }

  const e = error as { message?: string; hint?: string; details?: string; code?: string };

  const isNetwork =
    e.message === 'Failed to fetch' ||
    e.message?.includes('NetworkError') === true ||
    e.message?.includes('network') === true;

  if (isNetwork) {
    return {
      hint: 'network',
      message: 'Нет связи с сервером. Проверьте интернет.',
      isNetwork: true,
    };
  }

  return {
    hint: e.hint ?? null,
    message: cleanPgMessage(e.message ?? 'Не удалось выполнить запрос'),
    isNetwork: false,
  };
}

/** Убирает служебные префиксы Postgres из текста, который увидит клиент. */
function cleanPgMessage(message: string): string {
  return message
    .replace(/^(ERROR|FATAL):\s*/i, '')
    .replace(/^P0001:\s*/i, '')
    .trim();
}

/** Понятные подписи для hint-кодов, где текст БД слишком технический. */
export const ERROR_ACTIONS: Record<string, { title: string; action?: string }> = {
  slot_full: {
    title: 'Это время только что заняли',
    action: 'Выберите другое время',
  },
  slot_unavailable: {
    title: 'Выбранное время больше недоступно',
    action: 'Обновить список времени',
  },
  no_slots: {
    title: 'Сейчас заказов больше не принимают',
    action: 'Попробуйте позже',
  },
  item_unavailable: {
    title: 'Позиция закончилась',
    action: 'Уберите её из корзины',
  },
  modifier_required: { title: 'Нужно выбрать обязательные опции' },
  promo_invalid: { title: 'Промокод не найден' },
  promo_expired: { title: 'Срок промокода истёк' },
  promo_exhausted: { title: 'Промокод уже исчерпан' },
  promo_min_order: { title: 'Сумма заказа меньше минимальной для промокода' },
  delivery_min_order: { title: 'Сумма меньше минимальной для доставки' },
  delivery_zone_required: { title: 'Выберите зону доставки' },
  phone_required: { title: 'Для доставки нужен телефон' },
  address_required: { title: 'Для доставки нужен адрес' },
  cancel_too_late: {
    title: 'Заказ уже готовят',
    action: 'Позвоните на точку, чтобы отменить',
  },
  network: { title: 'Нет связи', action: 'Проверьте интернет' },
};
